import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { DashboardPageLayout } from '@/components/layouts/dashboard-page-layout'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useWindowSize } from 'react-use'
import { dashboardPageContainerStyle } from '@/constants/layout'
import { getSurveyAnswers, downloadSurveyCsv } from '@/apis/aipass'
import { SurveyAnswersResponse } from '@/models/survey/answers'
import { SurveyTab } from '@/components/pages/survey/tab'
import { SortKeyType } from '@/components/molecules/sortable-table-header'
import { SurveyAnswersHeader } from '@/components/organisms/survey/answers/header'
import { SurveyAnswersTable } from '@/components/organisms/survey/answers/table'

/**
 * 回答一覧ページのメインコンポーネント
 * アンケート回答データを取得し、テーブル形式で表示する
 */
export const SurveyAnswers: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const windows = useWindowSize()
  const [loading, setLoading] = useState<boolean>(true)
  const [dateFrom, setDateFrom] = useState<string | undefined>(dayjs().subtract(7, 'day').format('YYYY-MM-DD'))
  const [dateTo, setDateTo] = useState<string | undefined>(dayjs().format('YYYY-MM-DD'))
  const [page, setPage] = useState<number>(1)
  const [answersData, setAnswersData] = useState<SurveyAnswersResponse | null>(null)
  const [sortKey, setSortKey] = useState<SortKeyType>({ key: 'submittedAt', order: 'desc' })
  const [questionId, setQuestionId] = useState<string | undefined>(undefined)

  // アンケート回答データを取得
  const loadAnswers = async () => {
    try {
      setLoading(true)
      const params: Parameters<typeof getSurveyAnswers>[0] = {
        dateFrom,
        dateTo,
        page,
        sortKey: sortKey.key.startsWith('questionId-') ? 'questionId' : 'submittedAt',
        order: sortKey.order,
      }
      if (sortKey.key.startsWith('questionId-') && questionId) {
        params.questionId = questionId
      }
      const data = await getSurveyAnswers(params)
      setAnswersData(data)
    } catch (error) {
      errorHandler(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // 両方の日付が指定されている場合のみAPIリクエストを実行
    if (dateFrom && dateTo) {
      loadAnswers()
    }
  }, [dateFrom, dateTo, page, sortKey])

  // CSVファイルをダウンロード（Content-Dispositionヘッダーからファイル名を取得）
  const handleCsvDownload = async () => {
    try {
      const result = await downloadSurveyCsv({
        dateFrom,
        dateTo,
        lang: i18n.language as 'ja' | 'en' | 'zh' | 'zhTw' | 'ko',
        sortKey: 'submittedAt',
        order: 'desc',
      })

      const url = window.URL.createObjectURL(result.data)
      const link = document.createElement('a')
      link.href = url

      // Content-Dispositionヘッダーからファイル名を抽出
      // CORSでContent-Dispositionが公開されていない場合があるため、日付範囲からファイル名を生成
      const contentDisposition = result.headers['content-disposition'] || result.headers['Content-Disposition']
      let filename = 'surveys.csv'

      if (contentDisposition) {
        // クォート付き: filename="surveys.csv" または attachment; filename="surveys.csv"
        let filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        // クォートなし: filename=surveys.csv または attachment; filename=surveys.csv
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/)
        }
        // RFC 5987形式: filename*=UTF-8''surveys.csv
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/)
        }
        if (filenameMatch) {
          filename = filenameMatch[1].trim()
        }
      }

      // Content-Dispositionが取得できない場合（CORSで公開されていない場合）、日付範囲からファイル名を生成
      if (!contentDisposition || filename === 'surveys.csv') {
        if (dateFrom && dateTo) {
          const fromDate = dayjs(dateFrom).format('YYYYMMDD')
          const toDate = dayjs(dateTo).format('YYYYMMDD')
          const timestamp = dayjs().format('YYYYMMDDHHmmss')
          filename = `${timestamp}_surveys_${fromDate}_${toDate}.csv`
        } else {
          const timestamp = dayjs().format('YYYYMMDDHHmmss')
          filename = `${timestamp}_survey.csv`
        }
      }

      // ダウンロードリンクを作成してクリック
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      errorHandler(error)
    }
  }

  return (
    <DashboardPageLayout title={t('Survey')} loading={loading}>
      <>
        <div css={headerContainerStyle}>
          <SurveyTab currentTab="answers" />
        </div>
        <div css={[dashboardPageContainerStyle, css({ minHeight: '500px' })]}>
          <SurveyAnswersHeader
            dateFrom={dateFrom}
            dateTo={dateTo}
            onDateRangeChange={(from, to) => {
              setDateFrom(from)
              setDateTo(to)
            }}
            onCsvDownload={handleCsvDownload}
            answersData={answersData}
            currentPage={page}
            onPageChange={setPage}
          />
          {answersData && (
            <SurveyAnswersTable
              answersData={answersData}
              sortKey={sortKey}
              onSortChange={(newSortKey, questionId) => {
                setSortKey(newSortKey)
                setQuestionId(questionId)
                setPage(1) // ソート変更時は1ページ目に戻す
              }}
              tableHeight={windows.height - 200}
              currentQuestionId={questionId}
            />
          )}
          {!answersData && !loading && (
            <div css={noDataStyle}>
              <h1>{t('No data available')}</h1>
            </div>
          )}
        </div>
      </>
    </DashboardPageLayout>
  )
}

const noDataStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
})

const headerContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})
